import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIP_ORDER = ["tuna_whole", "tuna_seat", "wreck_day"];

export default function Manifest() {
  const { date } = useParams();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("yf_token");
    if (!token) { navigate("/admin/login"); return; }
    axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setBookings(r.data.bookings))
      .catch((e) => {
        if (e.response?.status === 401 || e.response?.status === 403) {
          localStorage.removeItem("yf_token");
          navigate("/admin/login");
        } else {
          setError("Could not load bookings.");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const dayBookings = useMemo(() =>
    (bookings || []).filter((b) => b.date === date && (b.status === "confirmed" || b.status === "pending")),
    [bookings, date]);

  const groups = useMemo(() => {
    const g = {};
    for (const b of dayBookings) {
      (g[b.trip_id] = g[b.trip_id] || { name: b.trip_name, id: b.trip_id, rows: [] }).rows.push(b);
    }
    return Object.values(g).sort((a, b) => TRIP_ORDER.indexOf(a.id) - TRIP_ORDER.indexOf(b.id));
  }, [dayBookings]);

  const totalSeats = dayBookings.reduce((s, b) => s + b.quantity, 0);
  const pretty = date && new Date(date + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  if (!bookings && !error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="manifest-loading">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 text-stone-900" data-testid="manifest-page">
      <div className="no-print bg-stone-950 border-b border-stone-800 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/admin" data-testid="manifest-back-link" className="text-xs text-stone-300 hover:text-amber-400 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Wheelhouse
          </Link>
          <button onClick={() => window.print()} data-testid="manifest-print-btn"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-amber-400 hover:bg-amber-300 text-stone-950 rounded transition-colors flex items-center gap-2">
            <Printer className="w-4 h-4" /> Print Manifest
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="bg-white border border-stone-300 shadow-sm p-8 sm:p-10 print:border-0 print:shadow-none print:p-0" data-testid="manifest-sheet">
          <div className="flex items-start justify-between border-b-2 border-stone-900 pb-5 mb-6">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Yellowfin Charters · Cougar Cat 10</div>
              <h1 className="text-2xl font-black uppercase tracking-tight mt-1">Day Manifest</h1>
              <div className="text-sm font-bold mt-1" data-testid="manifest-date">{pretty}</div>
            </div>
            <img src="/logo.png" alt="Yellowfin" className="w-14 h-14 rounded-full" />
          </div>

          {error && <div className="text-red-600 text-sm" data-testid="manifest-error">{error}</div>}

          {groups.length === 0 && !error && (
            <div className="text-stone-500 text-sm py-8 text-center border border-dashed border-stone-300 rounded" data-testid="manifest-empty">
              No bookings on this date.
            </div>
          )}

          {groups.map((g) => (
            <div key={g.id} className="mb-8" data-testid={`manifest-trip-${g.id}`}>
              <div className="flex justify-between items-baseline border-b border-stone-300 pb-1 mb-3">
                <h2 className="text-sm font-black uppercase tracking-wider">{g.name}</h2>
                <span className="text-xs font-mono text-stone-500">{g.rows.reduce((s, r) => s + r.quantity, 0)} seat(s)</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-mono uppercase tracking-widest text-stone-500 text-left">
                    <th className="py-1 pr-3">Lead Angler</th>
                    <th className="py-1 pr-3">Phone</th>
                    <th className="py-1 pr-3">Seats</th>
                    <th className="py-1 pr-3">Paid</th>
                    <th className="py-1">Boarded</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((b) => (
                    <tr key={b.booking_id} className="border-b border-stone-200" data-testid={`manifest-row-${b.booking_id}`}>
                      <td className="py-2.5 pr-3 font-semibold">{b.name}{b.status === "pending" && <span className="text-[10px] text-amber-600 font-mono ml-2">UNPAID HOLD</span>}</td>
                      <td className="py-2.5 pr-3 font-mono text-xs">{b.phone}</td>
                      <td className="py-2.5 pr-3">{b.quantity}</td>
                      <td className="py-2.5 pr-3">£{Number(b.amount_gbp).toLocaleString("en-GB")}</td>
                      <td className="py-2.5"><span className="inline-block w-5 h-5 border-2 border-stone-400 rounded-sm"></span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {groups.length > 0 && (
            <div className="flex justify-between items-center border-t-2 border-stone-900 pt-4 mt-2">
              <span className="text-xs font-mono uppercase tracking-widest text-stone-500">Total on board</span>
              <span className="text-lg font-black" data-testid="manifest-total">{totalSeats} angler{totalSeats === 1 ? "" : "s"}</span>
            </div>
          )}

          <div className="mt-10 pt-4 border-t border-stone-200 text-[10px] text-stone-500 flex justify-between">
            <span>Noss on Dart Marina, Dartmouth, Devon · Skipper 07929834128</span>
            <span>Printed {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
